"use client"
import React, { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import Icon from '@components/icon'
import Logo from '@components/station/logo'
import Banner from '@components/banner'
import PlayToggle from '@components/play/toggle'
import { usePlayer, useStorage } from '@hooks'
import { useAppDispatch } from '@redux/hook'
import { play } from '@redux/slices/playerSlice'
import { faBackwardStep, faForwardStep } from '@fortawesome/free-solid-svg-icons'
import styles from './category.module.scss'

/** The slice of IStation the hub needs. Kept deliberately narrow: this is a
 *  client component, so every field here is serialised into the RSC payload
 *  once per station. Articles, snippets and freqs stay on the server. */
export interface IHubStation {
    id: string
    name: string
    /** Stream url — what the player is handed on play. */
    url: string
    /** One high-signal line, already resolved through STATION_CARD_LINE. */
    blurb: string
}

export default function Hub({ stations }: { stations: IHubStation[] }) {
    const dispatch = useAppDispatch()
    const player = usePlayer()
    const { get } = useStorage("favorites")

    // Which station the big player is showing. Defaults to the first of the
    // category — the panel is the most prominent thing on the page, so it opens
    // with real content rather than an empty prompt. Deterministic, so the
    // server and first client render agree.
    const [selectedId, setSelectedId] = useState(stations[0]?.id)

    // Read once on mount rather than per render: localStorage is synchronous and
    // this list can run to 75 rows. Nothing renders differently on the server,
    // so there is nothing for hydration to disagree about — the hearts simply
    // appear on the first client render.
    const favorites = useMemo(() => (get() || []) as string[], [])

    const selected = useMemo(
        () => stations.find(station => station.id === selectedId) || stations[0],
        [stations, selectedId]
    )

    // Playback can start somewhere other than this page — a station page, or the
    // header — in which case the panel should follow it rather than sit on a
    // station that isn't on air.
    useEffect(() => {
        if (!player.url) return
        const playing = stations.find(station => station.url === player.url)
        if (playing && selected?.url !== player.url) setSelectedId(playing.id)
    }, [player.url, stations, selected])

    // Matching on url alone is not enough: two stations can share one stream —
    // נושמים מזרחית and מזרחית point at the same feed — and both tiles would
    // light up. The selection breaks the tie.
    const isLive = (station: IHubStation) =>
        Boolean(player.url) && station.url === player.url && station.id === selectedId

    const live = Boolean(selected) && isLive(selected)

    const skip = (offset: number) => {
        const index = stations.findIndex(station => station.id === selectedId)
        const next = stations[(index + offset + stations.length) % stations.length]
        if (!next) return
        setSelectedId(next.id)
        dispatch(play({ url: next.url, name: next.name, id: next.id }))
    }

    if (!selected) return null

    return (
        <div className={styles.zone}>
            {/* First in the DOM, so in RTL it takes the right-hand column. */}
            <ul className={styles.grid}>
                {
                    stations.map(station => (
                        // Same shape as the index on the home page: the tile is
                        // exactly as tall as the artwork, with the name and blurb
                        // sitting on it. The tile navigates; the button on top of
                        // it plays. One gesture each, no overlap.
                        <li key={station.id} className={`${styles.card} ${isLive(station)? styles.playing: ''}`}>
                            <Link href={`/station/${station.id}/`}>
                                <Logo station={station} lazy />
                                <span className={styles.caption}>
                                    <strong>{station.name}</strong>
                                    <small>{station.blurb}</small>
                                </span>
                            </Link>
                            <PlayToggle
                                url={station.url}
                                name={station.name}
                                id={station.id}
                                active={isLive(station)}
                                className={styles.cardPlay}
                                onPlay={() => setSelectedId(station.id)}
                            />
                            {
                                favorites.includes(station.id) && (
                                    <Icon icon={['fas', 'heart']} className={styles.heart} />
                                )
                            }
                        </li>
                    ))
                }
            </ul>

            <aside className={styles.side}>
                <div className={`${styles.player} ${live? styles.live: ''}`}>
                    <Link href={`/station/${selected.id}/`} className={styles.playerLogo} tabIndex={-1} aria-hidden>
                        {/* The LCP candidate on this page — the one logo that
                            should not wait for the lazy pass. */}
                        <Logo station={selected} lazy={false} />
                    </Link>
                    <div className={styles.playerInfo}>
                        <span className={styles.onair}>
                            {
                                live
                                    ? <><i className={styles.dot} /> משדר עכשיו</>
                                    : 'מוכן להשמעה'
                            }
                        </span>
                        <Link href={`/station/${selected.id}/`} className={styles.playerName}>
                            {selected.name}
                        </Link>
                        <small>{selected.blurb}</small>
                    </div>
                    <div className={styles.playerControls}>
                        <button
                            type="button"
                            className={styles.skip}
                            onClick={() => skip(-1)}
                            aria-label="התחנה הקודמת"
                            title="התחנה הקודמת"
                        >
                            {/* RTL: "previous" sits on the right, so the forward
                                glyph is the one that points there. */}
                            <Icon icon={faForwardStep} />
                        </button>
                        <PlayToggle
                            url={selected.url}
                            name={selected.name}
                            id={selected.id}
                            size="lg"
                            active={live}
                            onPlay={() => setSelectedId(selected.id)}
                        />
                        <button
                            type="button"
                            className={styles.skip}
                            onClick={() => skip(1)}
                            aria-label="התחנה הבאה"
                            title="התחנה הבאה"
                        >
                            <Icon icon={faBackwardStep} />
                        </button>
                    </div>
                </div>
                {/* The 300x600 rail unit, same slot the station pages use. The
                    stylesheet drops it once the rail stacks above the list —
                    a tall unit wedged between the sticky bar and the first row
                    pushes the stations off the screen. */}
                {/* <div className={styles.sideAd}>
                    <Banner slot='9619982722' />
                </div> */}
            </aside>
        </div>
    )
}
